import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  getNotifications,
  selectCurrentTeam,
  selectCurrentTeamJobPermissions,
  selectMerchantCustomizations,
  showNotifier
} from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { AddJobIcon, JobsTabIcon, NoSearchResultsIcon, SearchIcon } from 'assets/icons';
import { CustomButton, ErrorScreenWithRetry, If, Loader, Pagination, SearchWithDropdown } from 'components';
import routesPath from 'routes/RoutesPath';
import { JobStatus, JobsFilterType, NO_TAGS_FILTER_ID, jobSearchTypes } from './constant';
import { Tag } from 'containers/tags/tags.model';
import JobsFilter from './components/JobsFilter';
import JobsTable from './components/JobsTable';
import { emptyQueryParamsState, usePaginatedGetJobsQuery } from './hooks/usePaginatedGetJobsQuery';
import { ROOT_ROUTE_STATE } from 'routes/constants';
import isEqual from 'lodash/isEqual';
import {
  useLazyExportJobsQuery,
  useLazyGetJobsTeamViewsQuery,
  useToggleJobGroupByStatusMutation,
  useUpdateJobPaginationLimitMutation
} from './api';
import { useSaveCustomColumnsMutation } from 'containers/accounting/pay-runs/api';
import { NotifierTypes } from 'constants/notifierConstants';
import { resetCreateJobState } from './create-edit-job/createJobSlice';
import { JobsRouteState } from 'routes/types';
import { AmountFilter } from 'components/custom-amount-filter/CustomAmountFilter';
import { AmountFilterOperation } from 'constants/common';
import DateUtils from 'utils/dateUtils';
import { RelatedInvoiceForJob } from './jobs.model';
import DoneStatusSuccessModal from './components/DoneStatusSuccessModal';
import { InfoMesssages } from 'types/infoMessages';
import { debounce } from 'utils/commonUtils';
import JobsViewsBar from './components/JobsViewsBar';
import {
  clearAllViewFilters,
  selectJobsCurrentActiveView,
  selectJobsCurrentViewFilters,
  selectJobsInit,
  setJobsCurrentActiveView,
  setJobsCurrentViewFilters
} from './jobsSlice';
import { prepareEndDateForSave, prepareStartDateForSave, reverseAmountQueryParam } from 'containers/dispatch/utils';
import JobFilterActions from './components/JobFilterActions';
import EditStatusDetailModal from './components/EditStatusDetailModal';
import EditTableModal, { ColumnConfig } from './components/EditTableModal';
import JobsTableActions from './components/JobsTableActions';
import ModifyTagsModal from './components/ModifyTagsModal';
import { useGetTagsQuery } from 'containers/tags/api';
import BulkActionConfirmationModal from './components/BulkActionConfirmationModal';
import { useBulkActions } from './hooks/useBulkActions';
import { useJobSelection } from './hooks/useJobSelection';

// const JobsPageCTEvents = {
//   [JOB_TAB_TITLE.TO_BE_STARTED]: JobsCleverTapEvents.webJobsToBeStartedPageView,
//   [JOB_TAB_TITLE.IN_PROGRESS]: JobsCleverTapEvents.webJobsInProgressPageView,
//   [JOB_TAB_TITLE.PENDING]: JobsCleverTapEvents.webJobsPendingPageView,
//   [JOB_TAB_TITLE.ALL_JOBS]: JobsCleverTapEvents.webAllJobsPageView
// };

const Jobs: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const currentTeam = useAppSelector(selectCurrentTeam);
  const currentActiveView = useAppSelector(selectJobsCurrentActiveView);
  const currentViewFilters = useAppSelector(selectJobsCurrentViewFilters);
  const currentJobPermissions = useAppSelector(selectCurrentTeamJobPermissions);
  const { teamConfigs } = useAppSelector(selectMerchantCustomizations);
  const { groupByStatus, jobListPagination } = useAppSelector(selectJobsInit) ?? {
    groupByStatus: false,
    jobListPagination: 50
  };
  const notifications = useAppSelector(getNotifications);

  const [showStatusSuccessModal, setShowStatusSuccessModal] = useState(false);
  const [currentJobInvoice, setCurrentJobInvoice] = useState(null);
  const [rootJobId, setRootJobId] = useState('');
  const [amountFilter, setAmountFilter] = useState<AmountFilter>({
    filterOperation: AmountFilterOperation.ALL,
    minAmount: null,
    maxAmount: null
  });
  const [searchText, setSearchText] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedMerchants, setSelectedMerchants] = useState<string[]>([]);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<JobStatus[]>([]);
  const [leadSources, setLeadSources] = useState<string[]>([]);
  const [selectedServiceAreas, setSelectedServiceAreas] = useState<string[]>([]);
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState<string[]>([]);
  const [selectedEstimateStatus, setSelectedEstimateStatus] = useState<string[]>([]);
  const [clicked, setClicked] = useState(false);
  const [currentJobId, setCurrentJobId] = useState('');
  const [showFiltersAndExport, setShowFiltersAndExport] = useState<boolean>(false);
  const [scheduleStartDate, setScheduleStartDate] = useState<string | null>(null);
  const [scheduleEndDate, setScheduleEndDate] = useState<string | null>(null);
  const [createdStartDate, setCreatedStartDate] = useState<string | null>(null);
  const [createdEndDate, setCreatedEndDate] = useState<string | null>(null);
  const [externalCompanies, setExternalCompanies] = useState<string[]>([]);
  const [previousTeamId, setPreviousTeamId] = useState<number | null>(null);
  const [showEditTableModal, setShowEditTableModal] = useState(false);
  const [showModifyTagsModal, setShowModifyTagsModal] = useState(false);

  const [exportJobsQuery] = useLazyExportJobsQuery();
  const [getJobsTeamViewsQuery, { data: views, isFetching: isTeamViewsLoading }] = useLazyGetJobsTeamViewsQuery();
  const [updateJobPaginationLimit] = useUpdateJobPaginationLimitMutation();
  const [toggleJobGroupByStatus, { isLoading: isGroupByStatusLoading }] = useToggleJobGroupByStatusMutation();
  const [saveCustomColumns, { isLoading: isSavingCustomColumns }] = useSaveCustomColumnsMutation();
  const { data: tagsData, isFetching: isTagsLoading } = useGetTagsQuery({ teamId: currentTeam?.id });

  const {
    list: jobsList,
    isLoading: isLoadingJobs,
    fetchData,
    resetQuery: resetJobsQuery,
    setQueryParams: setJobQueryParams,
    queryParams: jobsQueryParams,
    totalListCount: jobsCount,
    isLoadingSuccess: jobsLoadingSuccess,
    hasPermissionsToView,
    searchType,
    page,
    setSearchType,
    isError: isJobsError,
    groupedJobsByStatus,
    columnConfig
  } = usePaginatedGetJobsQuery(jobListPagination);

  const [showEditStatusModal, setShowEditStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<JobStatus | null>(null);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [selectedJobNumber, setSelectedJobNumber] = useState('');
  const [isUnScheduled, setIsUnScheduled] = useState(false);
  const locationStateFiltersRef = useRef(location.state?.jobsFilters);
  const jobInitialStatus = teamConfigs?.jobInitialStatus;

  const {
    selectedJobs,
    isSelectAllAcrossPagination,
    deselectedJobIds,
    getEffectiveSelectedCount,
    handleJobCheckboxSelect,
    handleSelectAll,
    handleSelectAllAcrossPagination,
    clearAllSelections,
    isJobEffectivelySelected,
    handleSelectStatusGroup
  } = useJobSelection({
    jobsCount
  });

  useEffect(() => {
    if (!currentTeam?.id) return;

    const isTeamChange = previousTeamId !== currentTeam.id;
    if (isTeamChange) {
      // Reset view and clear all saved view filters on team change
      dispatch(clearAllViewFilters());
      dispatch(setJobsCurrentActiveView(null));
      setPreviousTeamId(currentTeam.id);
      getJobsTeamViewsQuery({ teamId: currentTeam.id, setNoFilters: locationStateFiltersRef.current });
    } else if (currentActiveView) {
      // Refresh views when active view changes (but not on team change)
      getJobsTeamViewsQuery({
        teamId: currentTeam.id,
        view: currentActiveView,
        setNoFilters: locationStateFiltersRef.current
      });
    }
  }, [currentTeam?.id, currentActiveView]);

  useEffect(() => {
    resetJobsQuery();
  }, [currentJobPermissions.manageOthersJobs, currentJobPermissions.createAndEditJob]);

  useEffect(() => {
    if ((currentViewFilters.customerIds as string[])?.length > 0) {
      setSelectedCustomers(currentViewFilters.customerIds as string[]);
    } else {
      setSelectedCustomers([]);
    }
    if ((currentViewFilters.createdBy as string[])?.length > 0) {
      setSelectedMerchants(currentViewFilters.createdBy as string[]);
    } else {
      setSelectedMerchants([]);
    }
    if ((currentViewFilters.assignees as string[])?.length > 0) {
      setSelectedTeamMembers(currentViewFilters.assignees as string[]);
    } else {
      setSelectedTeamMembers([]);
    }
    if ((currentViewFilters.leadSource as string[])?.length > 0) {
      setLeadSources(currentViewFilters.leadSource as string[]);
    } else {
      setLeadSources([]);
    }
    if ((currentViewFilters.status as string[])?.length > 0) {
      const filterStatuses = currentViewFilters.status as string[];
      const resultStatuses = filterStatuses.map(status => status as JobStatus);
      setStatuses(resultStatuses);
    } else {
      setStatuses([]);
    }
    if (currentViewFilters.scheduledFrom) {
      const startDateAtStartOfDay = DateUtils.getStartOfDay(currentViewFilters.scheduledFrom as string);
      const formattedDate = DateUtils.getDateInFormat({ date: startDateAtStartOfDay, dateFormat: 'yyyy-MM-dd' });
      setScheduleStartDate(formattedDate);
    } else {
      setScheduleStartDate(null);
    }
    if (currentViewFilters.scheduledTo) {
      const endDateAtEndOfDay = DateUtils.getEndOfDay(currentViewFilters.scheduledTo as string);
      const formattedDate = DateUtils.getDateInFormat({ date: endDateAtEndOfDay, dateFormat: 'yyyy-MM-dd' });
      setScheduleEndDate(formattedDate);
    } else {
      setScheduleEndDate(null);
    }
    if (currentViewFilters.createdFrom) {
      setCreatedStartDate(
        DateUtils.getDateInFormat({
          date: new Date(currentViewFilters.createdFrom as string),
          dateFormat: 'yyyy-MM-dd'
        })
      );
    } else {
      setCreatedStartDate(null);
    }
    if (currentViewFilters.createdTo) {
      setCreatedEndDate(
        DateUtils.getDateInFormat({
          date: new Date(currentViewFilters.createdTo as string),
          dateFormat: 'yyyy-MM-dd'
        })
      );
    } else {
      setCreatedEndDate(null);
    }
    if (currentViewFilters.amount) {
      const amountFilterToSet =
        typeof currentViewFilters.amount === 'string'
          ? reverseAmountQueryParam(currentViewFilters.amount)
          : currentViewFilters.amount;
      setAmountFilter(amountFilterToSet as AmountFilter);
    } else {
      setAmountFilter({
        filterOperation: AmountFilterOperation.ALL,
        minAmount: null,
        maxAmount: null
      });
    }
    if ((currentViewFilters.externalCompany as string[])?.length > 0) {
      setExternalCompanies(currentViewFilters.externalCompany as string[]);
    } else {
      setExternalCompanies([]);
    }
    if ((currentViewFilters.tags as string[])?.length > 0 || currentViewFilters.noTags) {
      const tagsToSet = currentViewFilters.noTags
        ? [NO_TAGS_FILTER_ID, ...(currentViewFilters.tags as string[])]
        : (currentViewFilters.tags as string[]);
      setSelectedTags(tagsToSet);
    } else {
      setSelectedTags([]);
    }
    if (currentViewFilters.isUnscheduled) {
      setIsUnScheduled(currentViewFilters.isUnscheduled as boolean);
    } else {
      setIsUnScheduled(false);
    }
    if ((currentViewFilters.serviceArea as string[])?.length > 0) {
      setSelectedServiceAreas(currentViewFilters.serviceArea as string[]);
    } else {
      setSelectedServiceAreas([]);
    }
    if ((currentViewFilters.invoiceStatus as string[])?.length > 0) {
      setSelectedInvoiceStatus(currentViewFilters.invoiceStatus as string[]);
    } else {
      setSelectedInvoiceStatus([]);
    }
    if ((currentViewFilters.estimateStatus as string[])?.length > 0) {
      setSelectedEstimateStatus(currentViewFilters.estimateStatus as string[]);
    } else {
      setSelectedEstimateStatus([]);
    }
  }, [currentViewFilters, currentActiveView]);

  useEffect(() => {
    if (locationStateFiltersRef.current && currentTeam?.id && currentActiveView) {
      const routeState = locationStateFiltersRef.current as JobsRouteState;
      let filtersToApply = { ...currentViewFilters };

      // Apply customer filter if present
      if (routeState?.customerId) {
        filtersToApply = {
          ...filtersToApply,
          customerIds: [routeState.customerId]
        };
      }

      // Apply date/status filters based on filter type
      if (routeState?.jobsFilterType) {
        if (routeState.jobsFilterType === JobsFilterType.NEXT_7_DAYS) {
          const currentDate = new Date();
          const oneWeekLaterDate = DateUtils.getDateInFormat({
            date: new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000),
            dateFormat: 'yyyy-MM-dd'
          });
          filtersToApply = {
            ...filtersToApply,
            status: [
              JobStatus.DONE_PENDING_APPROVAL,
              JobStatus.SUBMITTED,
              JobStatus.CONFIRMED,
              JobStatus.IN_PROGRESS,
              JobStatus.PENDING,
              JobStatus.DONE,
              ...(jobInitialStatus === JobStatus.INBOX ? [JobStatus.INBOX] : [])
            ],
            scheduledFrom: DateUtils.getDateInFormat({ date: currentDate, dateFormat: 'yyyy-MM-dd' }),
            scheduledTo: oneWeekLaterDate
          };
        } else if (routeState.jobsFilterType === JobsFilterType.OVERDUE) {
          const yesterday = DateUtils.getYesterday('yyyy-MM-dd') as string;
          filtersToApply = {
            ...filtersToApply,
            status: [
              JobStatus.DONE_PENDING_APPROVAL,
              JobStatus.SUBMITTED,
              JobStatus.CONFIRMED,
              JobStatus.IN_PROGRESS,
              JobStatus.PENDING,
              ...(jobInitialStatus === JobStatus.INBOX ? [JobStatus.INBOX] : [])
            ],
            scheduledTo: yesterday
          };
        } else if (routeState.jobsFilterType === JobsFilterType.UNSCHEDULED) {
          filtersToApply = {
            ...filtersToApply,
            isUnscheduled: true
          };
        }
      }
      // Apply member/assignee filter if present
      if (routeState?.memberId) {
        filtersToApply = {
          ...filtersToApply,
          assignees: [routeState.memberId]
        };
      }

      // Apply all collected filters at once
      dispatch(setJobsCurrentViewFilters(filtersToApply));

      locationStateFiltersRef.current = null;
      // Create a new state object without jobsFilters
      const newState = { ...location.state };
      delete newState.jobsFilters;

      // Replace the history state without triggering a re-render
      window.history.replaceState(newState, document.title);
    }
  }, [currentTeam?.id, currentActiveView]);

  useEffect(() => {
    const routeState = location.state?.jobsFilters as JobsRouteState;
    resetJobsQuery();
    if (!isInitialQueryParamsState() && !routeState?.jobsFilterType) {
      // setShowFiltersAndExport(false);
      setSelectedMerchants([]);
      setLeadSources([]);
      setScheduleEndDate(null);
      setScheduleStartDate(null);
    }
    if (searchText) setSearchText('');
    if (searchType !== jobSearchTypes[0]) setSearchType(jobSearchTypes[0]);
    if (!routeState?.customerId) {
      setSelectedCustomers([]);
    }
    if (!routeState?.memberId) {
      setSelectedTeamMembers([]);
    }
    // logAnalyticEvent(JobsPageCTEvents[currentTab]);
  }, [currentTeam?.id]);

  useEffect(() => {
    if (!isLoadingJobs) {
      setShowFiltersAndExport(true);
    }
  }, [isLoadingJobs]);

  // Clear selections when filters change
  useEffect(() => {
    clearAllSelections();
  }, [
    selectedCustomers,
    selectedMerchants,
    selectedTeamMembers,
    leadSources,
    statuses,
    amountFilter,
    scheduleEndDate,
    scheduleStartDate,
    externalCompanies,
    selectedTags,
    isUnScheduled,
    selectedServiceAreas,
    selectedInvoiceStatus,
    selectedEstimateStatus,
    currentActiveView?.id,
    searchText,
    createdStartDate,
    createdEndDate,
    clearAllSelections
  ]);

  useEffect(() => {
    const updatedParams = {
      ...jobsQueryParams,
      customerIds: selectedCustomers,
      assignees: selectedTeamMembers,
      createdBy: selectedMerchants,
      status: statuses,
      leadSource: leadSources,
      scheduledFrom: scheduleStartDate ? prepareStartDateForSave(scheduleStartDate) : null,
      scheduledTo: scheduleEndDate ? prepareEndDateForSave(scheduleEndDate) : null,
      amount: getAmountParam(),
      externalCompany: externalCompanies,
      tags: selectedTags.filter(tag => tag !== NO_TAGS_FILTER_ID),
      isUnscheduled: isUnScheduled,
      groupByStatus: groupByStatus,
      serviceAreaId: selectedServiceAreas,
      invoiceStatus: selectedInvoiceStatus,
      estimateStatus: selectedEstimateStatus,
      createdFrom: DateUtils.startDateToUTCString(createdStartDate),
      createdTo: DateUtils.endDateToUTCString(createdEndDate),
      viewId: currentActiveView?.id?.toString() || null,
      noTags: selectedTags.includes(NO_TAGS_FILTER_ID) || null
    };
    if (!locationStateFiltersRef.current && (!isEqual(updatedParams, jobsQueryParams) || searchText)) {
      setJobQueryParams(prevQueryParams => ({
        ...prevQueryParams,
        ...updatedParams
      }));
    }
  }, [
    selectedCustomers,
    selectedMerchants,
    selectedTeamMembers,
    leadSources,
    statuses,
    searchType,
    amountFilter,
    scheduleEndDate,
    scheduleStartDate,
    externalCompanies,
    selectedTags,
    isUnScheduled,
    groupByStatus,
    selectedServiceAreas,
    selectedInvoiceStatus,
    selectedEstimateStatus,
    currentActiveView?.id
  ]);

  const isSelected = (jobId: string) => {
    if (currentJobId === jobId) {
      return true;
    }
    return false;
  };

  const handleSearchIconClick = () => {
    setClicked(true);
  };

  const onJobClick = (jobId: string) => {
    if (currentJobId === jobId) return;
    setCurrentJobId(jobId);
    navigate(`${routesPath.JOBS}/${currentTeam.id}/${jobId}`, { state: ROOT_ROUTE_STATE });
  };

  const onEditStatusClick = (jobId: string, status: JobStatus, jobNumber: string) => {
    setSelectedJobId(jobId);
    setSelectedStatus(status);
    setSelectedJobNumber(jobNumber);
    setShowEditStatusModal(true);
  };

  const handleSidePanelClose = () => {
    setCurrentJobId('');
    navigate(routesPath.JOBS, { replace: true });
  };

  const isInitialQueryParamsState = () => {
    return isEqual(jobsQueryParams, emptyQueryParamsState);
  };

  const isFilterResultEmpty =
    jobsList?.length === 0 && !isLoadingJobs && !isInitialQueryParamsState() && jobsQueryParams.search === null;

  const isSearchResultEmpty =
    jobsList?.length === 0 && !isLoadingJobs && jobsQueryParams.search !== null && jobsQueryParams.search !== '';

  const isJobsResultEmpty =
    jobsList?.length === 0 &&
    !isLoadingJobs &&
    isInitialQueryParamsState() &&
    jobsQueryParams.search === null &&
    jobsLoadingSuccess;

  const isExportDisabled = isFilterResultEmpty;

  const initiateSearch = (searchTerm?: string) => {
    setJobQueryParams(prevQueryParams => ({ ...prevQueryParams, search: searchTerm }));
  };

  const debouncedSearchHandler = useCallback(debounce(initiateSearch, 500), [jobsQueryParams]);

  const onSearchTermChange = (searchTerm: string) => {
    setSearchText(searchTerm);
    debouncedSearchHandler(searchTerm);
  };

  const onClearSearch = () => {
    setSearchText('');
    initiateSearch(null);
  };

  const onInfoClick = (id: string, infoMessage) => {
    const existingNotification = notifications.find(notification => notification.id === id);
    if (existingNotification) return;
    dispatch(
      showNotifier({
        id,
        message: {
          primaryMessage: infoMessage,
          secondaryMessage: '',
          isMessageHtml: true
        },
        type: NotifierTypes.WARNING,
        showClose: true,
        fontStyle: 'text-primary font-normal'
      })
    );
  };

  const onExportClick = () => {
    if (isExportDisabled) {
      onInfoClick('jobs-export', InfoMesssages.noJobsToExport);
      return;
    }
    const { isUnscheduled, ...rest } = jobsQueryParams;
    exportJobsQuery({
      teamId: currentTeam?.id,
      ...rest,
      ...(isUnscheduled ? { isUnscheduled: true } : {}),
      limit: null,
      page: null
    });
    dispatch(
      showNotifier({
        message: {
          primaryMessage: 'Downloading Jobs may take few seconds',
          secondaryMessage: ''
        },
        type: NotifierTypes.INFO,
        showClose: false,
        fontStyle: 'text-primary font-normal'
      })
    );
  };

  const onCreateJob = () => {
    dispatch(resetCreateJobState());
    navigate(routesPath.CREATE_JOB);
  };

  const refetchJobsList = () => {
    fetchData(page);
  };

  const getAmountParam = () => {
    const { filterOperation, minAmount, maxAmount } = amountFilter;
    if (filterOperation === AmountFilterOperation.ALL) return null;
    if (filterOperation === AmountFilterOperation.BW) return `${filterOperation}:${minAmount}:${maxAmount}`;
    return `${filterOperation}:${minAmount}`;
  };

  const updateCurrentJobInvoice = (invoice: RelatedInvoiceForJob, jobId: string) => {
    setRootJobId(jobId);
    setCurrentJobInvoice(invoice);
    setShowStatusSuccessModal(true);
  };

  const handleInvoiceClick = (invoiceId: string) => {
    navigate(`/jobs/${currentTeam.id}/${rootJobId}/invoice/${invoiceId}`, {
      state: ROOT_ROUTE_STATE
    });
  };

  const handleAddorViewInvoice = () => {
    if (currentJobInvoice) {
      handleInvoiceClick(currentJobInvoice.id);
    } else {
      navigate(`/invoices/create?jobId=${currentJobInvoice.id}`);
    }
    setShowStatusSuccessModal(false);
  };

  const onStatusSuccessModalClose = () => {
    setCurrentJobInvoice(null);
    setRootJobId('');
    setShowStatusSuccessModal(false);
  };

  const handleEditStatusModalClose = () => {
    setShowEditStatusModal(false);
    setSelectedStatus(null);
    setSelectedJobId('');
    setSelectedJobNumber('');
  };

  const handleEditStatusSave = () => {
    refetchJobsList();
    handleEditStatusModalClose();
  };

  const handleSortChange = (sortBy: string, sortOrder: string) => {
    setJobQueryParams(prevParams => ({
      ...prevParams,
      sortBy,
      sortOrder
    }));
  };

  const onEditTableClick = () => {
    setShowEditTableModal(true);
  };

  const handleEditTableModalClose = () => {
    setShowEditTableModal(false);
  };

  const handleEditJobPaginationLimit = async (pageSize: number) => {
    await updateJobPaginationLimit({ teamId: currentTeam.id, pageSize });
    setJobQueryParams(prevQueryParams => ({
      ...prevQueryParams,
      limit: pageSize
    }));
  };

  const handleEditTableSave = async (isGroupByOn: boolean, columns: ColumnConfig[]) => {
    // Handle group by status update
    await toggleJobGroupByStatus({ teamId: currentTeam.id, groupByStatus: isGroupByOn });

    // Save column configuration
    const feature = `JOBS_${currentActiveView?.id}`;

    await saveCustomColumns({
      teamId: currentTeam.id,
      payload: {
        feature,
        columnInfo: columns
      }
    });

    // Update query params to trigger refetch with new settings
    setJobQueryParams(prevQueryParams => ({
      ...prevQueryParams,
      groupByStatus: isGroupByOn
    }));
    handleEditTableModalClose();
  };

  const getCurrentPageJobs = () => {
    return groupByStatus ? Object.values(groupedJobsByStatus || {}).flat() : jobsList || [];
  };

  const areAllCurrentPageJobsSelected = () => {
    const currentPageJobs = getCurrentPageJobs();
    if (currentPageJobs.length === 0) return false;

    const currentPageJobIds = currentPageJobs.map(job => job.id);
    const selectedCurrentPageJobs = selectedJobs.filter(job => currentPageJobIds.includes(job.id));
    return currentPageJobIds.length === selectedCurrentPageJobs.length;
  };

  const handleModifyTagsClick = () => {
    setShowModifyTagsModal(true);
  };

  const handleModifyTagsClose = () => {
    setShowModifyTagsModal(false);
  };

  const handleUpdateTagsWithClose = async (tagsToAdd: Tag[], removeCurrentTags: boolean) => {
    const response = await handleUpdateTags(tagsToAdd, removeCurrentTags);
    if (response) {
      handleModifyTagsClose();
    }
  };

  const handleBulkActionComplete = () => {
    clearAllSelections();
    refetchJobsList();
  };

  const {
    bulkActionState,
    showBulkActionModal,
    isActionUpdating,
    handleRescheduleJobs,
    handleChangeJobStatus,
    handleReassignJobs,
    handleUpdateTags,
    handleBulkActionModalClose,
    handleBulkActionConfirm
  } = useBulkActions({
    teamId: currentTeam?.id,
    selectedJobIds: selectedJobs.map(job => job.id),
    isSelectAllAcrossPagination,
    deselectedJobIds,
    onActionComplete: handleBulkActionComplete,
    queryParams: jobsQueryParams,
    searchType
  });

  return (
    <>
      <div
        className="customNormalScroll relative flex h-full 
        w-full flex-col overflow-y-auto pt-5 pb-0">
        <div className="w-full">
          <div className="mb-3 flex flex-row items-center gap-3 px-[30px]">
            <div className="text-2xl font-bold text-primaryText">Jobs</div>
            <If condition={!clicked}>
              <div onClick={handleSearchIconClick}>
                <SearchIcon className="mx-2 flex lg:hidden" />
              </div>
            </If>
            <SearchWithDropdown
              showSuggestionList={false}
              selectedOption={searchType}
              setSelectedOption={setSearchType}
              menuItems={jobSearchTypes}
              searchId="invoice-search"
              onClearSearchInput={onClearSearch}
              onSearchInputChange={onSearchTermChange}
              searchInputvalue={searchText}
              wrapperStyle={`w-38 cursor-pointer ${!clicked ? 'lg:flex sm:hidden' : ''} `}
              dropDownStyle="w-[140px]"
              autoFocus={true}
            />
            <div className="ml-auto self-end">
              <CustomButton
                id="create-job-button"
                onClick={onCreateJob}
                className="h-[38px] text-primaryBtnText"
                showDisabledReason={!currentJobPermissions.createAndEditJob}
                disabledReason={InfoMesssages.createJobNoPermissionMessage}
                StartIcon={AddJobIcon}
                childrenContainerStyles="mr-1">
                <div className="text-sbase font-semibold">Create job</div>
              </CustomButton>
            </div>
          </div>

          <JobsViewsBar views={views?.data || []} isTeamViewsLoading={isTeamViewsLoading} />
          <If condition={showFiltersAndExport}>
            <div className="flex w-full justify-between gap-4 border-y border-secondaryBtn pt-3 pb-2.5 pl-6 pr-3">
              <div className="flex w-full flex-wrap gap-3">
                <JobsFilter
                  statuses={statuses}
                  setStatuses={setStatuses}
                  currentTeamId={currentTeam?.id}
                  selectedCustomers={selectedCustomers}
                  setSelectedCustomers={setSelectedCustomers}
                  selectedMerchants={selectedMerchants}
                  setSelectedMerchants={setSelectedMerchants}
                  selectedTeamMembers={selectedTeamMembers}
                  setSelectedTeamMembers={setSelectedTeamMembers}
                  leadSources={leadSources}
                  setLeadSources={setLeadSources}
                  amountFilter={amountFilter}
                  setAmountFilter={setAmountFilter}
                  scheduleEndDate={scheduleEndDate}
                  setScheduleEndDate={setScheduleEndDate}
                  scheduleStartDate={scheduleStartDate}
                  setScheduleStartDate={setScheduleStartDate}
                  createdStartDate={createdStartDate}
                  setCreatedStartDate={setCreatedStartDate}
                  createdEndDate={createdEndDate}
                  setCreatedEndDate={setCreatedEndDate}
                  externalCompanies={externalCompanies}
                  setExternalCompanies={setExternalCompanies}
                  selectedTags={selectedTags}
                  setSelectedTags={setSelectedTags}
                  showStatusFilter={true}
                  showTeamFilter={currentJobPermissions?.manageOthersJobs}
                  selectedServiceAreas={selectedServiceAreas}
                  setSelectedServiceAreas={setSelectedServiceAreas}
                  selectedInvoiceStatus={selectedInvoiceStatus}
                  setSelectedInvoiceStatus={setSelectedInvoiceStatus}
                  selectedEstimateStatus={selectedEstimateStatus}
                  setSelectedEstimateStatus={setSelectedEstimateStatus}
                />
              </div>
              <JobFilterActions />
            </div>
          </If>
        </div>
        <If condition={isLoadingJobs}>
          <Loader />
        </If>
        <If condition={!isLoadingJobs && isJobsError && hasPermissionsToView}>
          <ErrorScreenWithRetry handleReload={refetchJobsList} />
        </If>
        {jobsLoadingSuccess && !isLoadingJobs && (
          <>
            <If condition={isJobsResultEmpty && hasPermissionsToView}>
              <div className="flex h-full flex-col items-center justify-center gap-5">
                <div className="flex flex-col items-center gap-5">
                  <JobsTabIcon className="h-10 w-10" />
                  <div className="text-sbase text-primaryText">No jobs found</div>
                </div>
              </div>
            </If>
            <If condition={isFilterResultEmpty && hasPermissionsToView}>
              <div className="flex h-full flex-col items-center justify-center gap-5">
                <div className="flex flex-col items-center gap-5">
                  <JobsTabIcon className="h-10 w-10" />
                  <div className="text-sbase text-primaryText">No jobs found for selected filters</div>
                </div>
              </div>
            </If>

            <If condition={isSearchResultEmpty && hasPermissionsToView}>
              <div className="flex h-full flex-col items-center justify-center gap-5">
                <div className="flex flex-col items-center gap-5">
                  <NoSearchResultsIcon />
                  <div className="text-sbase text-primaryText">No results found!</div>
                </div>
              </div>
            </If>

            <If condition={jobsList?.length !== 0 && hasPermissionsToView}>
              <JobsTableActions
                selectedJobs={selectedJobs}
                onClearSelection={clearAllSelections}
                onExportClick={onExportClick}
                onEditTableClick={onEditTableClick}
                onModifyTagsClick={handleModifyTagsClick}
                onRescheduleJobs={handleRescheduleJobs}
                onChangeJobStatus={handleChangeJobStatus}
                onReassignJobs={handleReassignJobs}
                isActionUpdating={isActionUpdating}
                totalJobsCount={jobsCount}
                isSelectAllAcrossPagination={isSelectAllAcrossPagination}
                onSelectAllAcrossPagination={handleSelectAllAcrossPagination}
                showSelectAllAcrossPagination={areAllCurrentPageJobsSelected() && !isSelectAllAcrossPagination}
                effectiveSelectedCount={getEffectiveSelectedCount()}
              />
              <div className="flex-1">
                <JobsTable
                  isSelected={isSelected}
                  jobsList={jobsList}
                  groupedJobsByStatus={groupedJobsByStatus}
                  onJobClick={onJobClick}
                  fetchData={fetchData}
                  jobsCount={jobsCount}
                  page={page}
                  onEditStatusClick={onEditStatusClick}
                  onSortChange={handleSortChange}
                  currentSortBy={jobsQueryParams.sortBy}
                  currentSortOrder={jobsQueryParams.sortOrder}
                  isGroupByStatus={groupByStatus}
                  columnConfig={columnConfig}
                  selectedJobs={selectedJobs}
                  onJobCheckboxSelect={handleJobCheckboxSelect}
                  onSelectAll={() => handleSelectAll(getCurrentPageJobs())}
                  onSelectStatusGroup={handleSelectStatusGroup}
                  isSelectAllAcrossPagination={isSelectAllAcrossPagination}
                  deselectedJobIds={deselectedJobIds}
                  isJobEffectivelySelected={isJobEffectivelySelected}
                  stickyHorizontalScrollbar={{ bottomOffsetPx: 48 }}
                  highlightedRowId={currentJobId || null}
                />
              </div>
            </If>
          </>
        )}
        <If condition={jobsList?.length !== 0 && hasPermissionsToView}>
          <div className="sticky bottom-0 z-10 w-full">
            <Pagination
              currentPage={page}
              onPageChange={fetchData}
              totalCount={jobsCount}
              limit={jobListPagination}
              showSelectResultsPerPage={true}
              wrapperClassName="w-full h-12 pr-16"
              onSelectResultsPerPage={handleEditJobPaginationLimit}
            />
          </div>
        </If>
        <If condition={!isLoadingJobs && !hasPermissionsToView}>
          <div className="flex h-full flex-col items-center justify-center gap-5">
            <div className="flex flex-col items-center gap-5">
              <JobsTabIcon className="h-10 w-10" />
              <div className="text-sbase text-primaryText">You do not have permissions to view jobs</div>
            </div>
          </div>
        </If>
      </div>
      {showEditStatusModal && selectedStatus && (
        <EditStatusDetailModal
          status={selectedStatus}
          onBackClick={handleEditStatusModalClose}
          onSave={handleEditStatusSave}
          jobId={selectedJobId}
          teamId={currentTeam?.id}
          jobNumber={selectedJobNumber}
          onClose={handleEditStatusModalClose}
          isRootState={true}
        />
      )}
      {showStatusSuccessModal && (
        <DoneStatusSuccessModal
          onAddInvoiceClick={handleAddorViewInvoice}
          invoice={currentJobInvoice}
          onClose={onStatusSuccessModalClose}
        />
      )}
      {showEditTableModal && (
        <EditTableModal
          onClose={handleEditTableModalClose}
          onSave={handleEditTableSave}
          isSaving={isGroupByStatusLoading || isSavingCustomColumns}
          initialGroupByStatus={groupByStatus}
          columns={columnConfig}
          reorderableStartIndex={1}
        />
      )}
      {showModifyTagsModal && (
        <ModifyTagsModal
          open={showModifyTagsModal}
          onClose={handleModifyTagsClose}
          allTags={tagsData?.data?.tags || []}
          isLoading={isTagsLoading}
          onUpdateTags={handleUpdateTagsWithClose}
          isSaving={isActionUpdating}
        />
      )}
      {showBulkActionModal && bulkActionState && (
        <BulkActionConfirmationModal
          open={showBulkActionModal}
          onClose={handleBulkActionModalClose}
          onConfirm={handleBulkActionConfirm}
          bulkState={bulkActionState}
          isLoading={isActionUpdating}
        />
      )}
      <Outlet context={{ handleSidePanelClose, refetchJobsList, updateCurrentJobInvoice }} />
    </>
  );
};

export default Jobs;
